import { Component, Input, OnChanges, SimpleChanges, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CallAuditTrackingService } from '../../services/call-audit-tracking.service';
import { CallAuditActivity, CallAuditEventType } from '../../models/call-audit-activity.model';
import { AuditActivityTracker } from '../../services/audit-activity-tracker';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';

/**
 * Read-only panel shown below an audit report: the reviewer activity timeline
 * plus per-reviewer active QA handling time. Modeled on `AuditLogPanelComponent`.
 */
@Component({
  selector: 'app-call-audit-activity-panel',
  standalone: true,
  imports: [CommonModule, IconComponentV2],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './call-audit-activity-panel.component.html',
})
export class CallAuditActivityPanelComponent implements OnChanges {
  private tracking = inject(CallAuditTrackingService);
  private tracker = inject(AuditActivityTracker, { optional: true });

  @Input() auditId: string | null = null;
  /** Start expanded so the activity is visible under the report by default. */
  @Input() expanded = false;

  isExpanded = signal(false);
  isLoading = signal(false);
  hasError = signal(false);
  activity = signal<CallAuditActivity | null>(null);

  expandedSessions = signal<Set<string>>(new Set());
  /** Sessions with no events have no key. */
  eventsBySession = signal<Partial<Record<string, any[]>>>({});
  unassignedEvents = signal<any[]>([]);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expanded']) this.isExpanded.set(this.expanded);
    if (changes['auditId']) {
      const id = changes['auditId'].currentValue;
      if (id) {
        this.fetch();
      } else {
        this.activity.set(null);
      }
    }
  }

  /** Re-fetch the activity — call after the audit is verified. */
  refresh(): void {
    if (this.auditId) this.fetch();
  }

  toggleExpand(): void {
    this.isExpanded.update((v) => !v);
  }

  toggleSession(sessionId: string): void {
    this.expandedSessions.update(set => {
      const newSet = new Set(set);
      if (newSet.has(sessionId)) newSet.delete(sessionId);
      else newSet.add(sessionId);
      return newSet;
    });
  }

  isSessionExpanded(sessionId: string): boolean {
    return this.expandedSessions().has(sessionId);
  }

  private fetch(): void {
    if (!this.auditId) return;
    this.isLoading.set(true);
    this.hasError.set(false);
    this.tracking.getAuditActivity(this.auditId).subscribe({
      next: (res: any) => {
        const data = res?.events ? res : res?.data;
        const activityData = data ?? { events: [], reviewTimes: [], totalActiveMs: 0 };

        // Do not filter out the currently active session so it populates in the sessions section.
        // if (this.tracker?.currentSessionId) {
        //   activityData.reviewTimes = activityData.reviewTimes.filter(
        //     (rt: any) => rt.sessionId !== this.tracker!.currentSessionId
        //   );
        // }

        // Sort by the most recent activity first (descending)
        activityData.reviewTimes.sort((a: any, b: any) => {
          const timeA = new Date(a.lastActivityAt || a.firstOpenedAt || 0).getTime();
          const timeB = new Date(b.lastActivityAt || b.firstOpenedAt || 0).getTime();
          return timeB - timeA;
        });

        this.activity.set(activityData);

        const grouped: Record<string, any[]> = {};
        const unassigned: any[] = [];
        
        const allowedEvents = ['AUDIT_OPENED', 'AUDIT_CLOSED', 'AUDIT_VERIFIED'];
        const filteredEvents = (activityData.events || []).filter((ev: any) => allowedEvents.includes(ev.eventType));

        for (const ev of filteredEvents) {
          if (ev.sessionId) {
            if (!grouped[ev.sessionId]) grouped[ev.sessionId] = [];
            grouped[ev.sessionId].push(ev);
          } else {
            unassigned.push(ev);
          }
        }
        this.eventsBySession.set(grouped);
        this.unassignedEvents.set(unassigned);

        this.isLoading.set(false);
      },
      error: () => {
        this.hasError.set(true);
        this.isLoading.set(false);
      },
    });
  }

  // ---- Display helpers ------------------------------------------------------

  formatDuration(ms: number | null | undefined): string {
    if (!ms || ms < 1000) return '0s';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const parts: string[] = [];
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    if (s || parts.length === 0) parts.push(`${s}s`);
    return parts.join(' ');
  }

  eventLabel(type: CallAuditEventType): string {
    const map: Record<CallAuditEventType, string> = {
      AUDIT_OPENED: 'Audit Started',
      AUDIT_CLOSED: 'Hard Close',
      TRANSCRIPT_VIEWED: 'Viewed transcript',
      AUDIO_PLAYED: 'Played audio',
      AUDIO_PAUSED: 'Paused audio',
      AUDIO_SEEKED: 'Seeked audio',
      REPORT_DOWNLOADED: 'Downloaded report',
      TRANSCRIPT_DOWNLOADED: 'Downloaded transcript',
      AUDIT_VERIFIED: 'Audit verified',
    };
    return map[type] || type;
  }

  eventDotClass(type: CallAuditEventType): string {
    switch (type) {
      case 'AUDIT_VERIFIED':
      case 'AUDIT_OPENED':
        return 'bg-green-500';
      case 'AUDIT_CLOSED':
        return 'bg-slate-400';
      case 'AUDIO_PLAYED':
      case 'AUDIO_PAUSED':
      case 'AUDIO_SEEKED':
        return 'bg-purple-500';
      case 'TRANSCRIPT_VIEWED':
        return 'bg-blue-500';
      case 'REPORT_DOWNLOADED':
      case 'TRANSCRIPT_DOWNLOADED':
        return 'bg-amber-500';
      default:
        return 'bg-slate-400';
    }
  }
}
